import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '../store/gameStore';

interface SkillNode {
  id: string;
  name: string;
  description: string;
  icon: string;
  cost: number;
  duration: number;
  bonusType: string;
  bonusValue: number;
  prerequisites: string[];
  position: { x: number; y: number };
  tier: number;
}

const CompanionResearch: React.FC = () => {
  const {
    companions,
    coins,
    formatNumber,
    startResearch,
    updateResearch,
    getResearchProjects,
    getGlobalBonuses,
    canResetSkillTree,
    resetSkillTree,
    getSkillTreeResetInfo,
  } = useGameStore();

  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Initial zoom abhängig vom Viewport (auf Mobile etwas näher heranzoomen für bessere Lesbarkeit)
  const isClient = typeof window !== 'undefined';
  // Noch größere Start-Zooms auf Mobile, damit der Baum direkt lesbar ist
  const defaultZoom = isClient ? (window.innerWidth <= 640 ? 1.8 : 1.1) : 1.1;
  const [zoomLevel, setZoomLevel] = useState(defaultZoom);
  // Startet zentriert
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  // Mobile optimizations
  const [isMobileView, setIsMobileView] = useState<boolean>(isClient ? window.innerWidth <= 640 : false);
  // Dynamischer Node-Radius je nach Viewport (auf Mobile deutlich kleiner, um Überlappung zu vermeiden)
  const nodeRadius = isMobileView ? 34 : 50;
  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
  const [touchInitialZoom, setTouchInitialZoom] = useState<number | null>(null);
  const [touchDistance, setTouchDistance] = useState<number | null>(null);
  const [touchStart, setTouchStart] = useState<{ x: number, y: number } | null>(null);
  const [lastTapTime, setLastTapTime] = useState<number | null>(null);

  // Bottom-sheet swipe state
  const bottomSheetTouchStartY = useRef<number | null>(null);

  useEffect(() => {
    if (!isClient) return;
    const onResize = () => {
      const mobile = window.innerWidth <= 640;
      setIsMobileView(mobile);
      setZoomLevel(mobile ? 0.55 : 0.75);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isClient]);

  // Update Forschung jede Sekunde
  useEffect(() => {
    const interval = setInterval(() => {
      updateResearch();
    }, 1000);

    return () => clearInterval(interval);
  }, [updateResearch]);

  const researchProjects = getResearchProjects();
  const globalBonuses = getGlobalBonuses();
  const skillTreeResetInfo = getSkillTreeResetInfo();
  // Definiere den Skill-Tree
  const skillTree: SkillNode[] = [
    { id: 'attack_boost_1', name: 'Grundangriff', description: 'Erhöht den Angriff aller Begleiter', icon: '⚔️', cost: 1000, duration: 5, bonusType: 'attack', bonusValue: 5, prerequisites: [], position: { x: 400, y: 1000 }, tier: 1 },
    { id: 'defense_boost_1', name: 'Grundverteidigung', description: 'Erhöht die Verteidigung aller Begleiter', icon: '🛡️', cost: 1000, duration: 5, bonusType: 'defense', bonusValue: 5, prerequisites: [], position: { x: 200, y: 1000 }, tier: 1 },
    { id: 'hp_boost_1', name: 'Vitalität', description: 'Erhöht die Gesundheit aller Begleiter', icon: '❤️', cost: 1000, duration: 5, bonusType: 'hp', bonusValue: 10, prerequisites: [], position: { x: 600, y: 1000 }, tier: 1 },
    { id: 'precision_1', name: 'Präzision', description: 'Erhöht den Basisschaden leicht', icon: '🎯', cost: 800, duration: 4, bonusType: 'attack', bonusValue: 3, prerequisites: [], position: { x: 100, y: 1000 }, tier: 1 },
    { id: 'steadiness_1', name: 'Standhaft', description: 'Leichter Verteidigungsbonus für alle Begleiter', icon: '🪵', cost: 800, duration: 4, bonusType: 'defense', bonusValue: 3, prerequisites: [], position: { x: 700, y: 1000 }, tier: 1 },

    { id: 'attack_boost_2', name: 'Verstärkter Angriff', description: 'Weiterer Angriffsbonus', icon: '⚔️', cost: 2500, duration: 10, bonusType: 'attack', bonusValue: 10, prerequisites: ['attack_boost_1'], position: { x: 400, y: 900 }, tier: 2 },
    { id: 'defense_boost_2', name: 'Verstärkte Verteidigung', description: 'Weiterer Verteidigungsbonus', icon: '🛡️', cost: 2500, duration: 10, bonusType: 'defense', bonusValue: 10, prerequisites: ['defense_boost_1'], position: { x: 200, y: 900 }, tier: 2 },
    { id: 'hp_boost_2', name: 'Erweiterte Vitalität', description: 'Weiterer Gesundheitsbonus', icon: '❤️', cost: 2500, duration: 10, bonusType: 'hp', bonusValue: 20, prerequisites: ['hp_boost_1'], position: { x: 600, y: 900 }, tier: 2 },
    { id: 'crit_chance_2', name: 'Kritische Treffer', description: 'Erhöht die Chance auf Spezialfähigkeiten', icon: '💥', cost: 3000, duration: 12, bonusType: 'abilityChance', bonusValue: 7, prerequisites: ['attack_boost_1'], position: { x: 100, y: 900 }, tier: 2 },
    { id: 'quick_learning_2', name: 'Schnelles Lernen', description: 'Verbessert Erfahrungserwerb leicht', icon: '📚', cost: 3000, duration: 12, bonusType: 'experience', bonusValue: 15, prerequisites: ['hp_boost_1'], position: { x: 700, y: 900 }, tier: 2 },

    { id: 'experience_boost_1', name: 'Lerntempo', description: 'Begleiter erhalten mehr Erfahrung', icon: '📚', cost: 5000, duration: 15, bonusType: 'experience', bonusValue: 25, prerequisites: ['attack_boost_2', 'defense_boost_2'], position: { x: 300, y: 800 }, tier: 3 },
    { id: 'ability_chance_1', name: 'Fähigkeitsmeisterung', description: 'Erhöht die Chance auf Spezialfähigkeiten', icon: '✨', cost: 5000, duration: 15, bonusType: 'abilityChance', bonusValue: 15, prerequisites: ['attack_boost_2', 'hp_boost_2'], position: { x: 500, y: 800 }, tier: 3 },
    { id: 'loot_chance_3', name: 'Beutefinder', description: 'Erhöht Chance auf bessere Beute (Fähigkeiten/Boni)', icon: '🧭', cost: 6000, duration: 18, bonusType: 'abilityChance', bonusValue: 12, prerequisites: ['experience_boost_1'], position: { x: 100, y: 800 }, tier: 3 },
    { id: 'cooldown_reduction_3', name: 'Schnellere Fähigkeiten', description: 'Reduziert Fähigkeitendauer/Abklingzeiten leicht', icon: '⏱️', cost: 6000, duration: 18, bonusType: 'abilityChance', bonusValue: 10, prerequisites: ['ability_chance_1'], position: { x: 700, y: 800 }, tier: 3 },

    { id: 'combat_mastery', name: 'Kampfmeisterschaft', description: 'Ultimative Kampffähigkeiten', icon: '🏹', cost: 10000, duration: 20, bonusType: 'attack', bonusValue: 25, prerequisites: ['experience_boost_1', 'ability_chance_1'], position: { x: 400, y: 700 }, tier: 4 },
    { id: 'meditation_mastery', name: 'Meditations-Meisterschaft', description: 'Spirituelle Erleuchtung', icon: '🧘', cost: 8000, duration: 18, bonusType: 'experience', bonusValue: 50, prerequisites: ['experience_boost_1'], position: { x: 100, y: 700 }, tier: 4 },
    { id: 'arena_mastery', name: 'Arena-Meisterschaft', description: 'Perfekte Kampftechniken', icon: '🏟️', cost: 12000, duration: 25, bonusType: 'defense', bonusValue: 30, prerequisites: ['ability_chance_1'], position: { x: 700, y: 700 }, tier: 4 },

    { id: 'divine_bond', name: 'Göttliche Verbindung', description: 'Transzendentale Macht', icon: '🌟', cost: 20000, duration: 30, bonusType: 'abilityChance', bonusValue: 35, prerequisites: ['combat_mastery', 'meditation_mastery'], position: { x: 250, y: 600 }, tier: 5 },
    { id: 'ancient_wisdom', name: 'Uralte Weisheit', description: 'Wissen der Ahnen', icon: '📜', cost: 25000, duration: 35, bonusType: 'experience', bonusValue: 100, prerequisites: ['combat_mastery', 'arena_mastery'], position: { x: 550, y: 600 }, tier: 5 },

    { id: 'transcendence', name: 'Transzendenz', description: 'Überwindung aller Grenzen', icon: '💫', cost: 50000, duration: 60, bonusType: 'attack', bonusValue: 100, prerequisites: ['divine_bond', 'ancient_wisdom'], position: { x: 400, y: 500 }, tier: 6 },

    { id: 'cosmic_awakening', name: 'Kosmische Erweckung', description: 'Erwachen kosmischer Kräfte', icon: '🌌', cost: 75000, duration: 90, bonusType: 'defense', bonusValue: 150, prerequisites: ['transcendence'], position: { x: 250, y: 400 }, tier: 7 },
    { id: 'eternal_wisdom', name: 'Ewige Weisheit', description: 'Wissen der Unsterblichen', icon: '📜', cost: 80000, duration: 100, bonusType: 'experience', bonusValue: 200, prerequisites: ['transcendence'], position: { x: 550, y: 400 }, tier: 7 },
    { id: 'divine_perfection', name: 'Göttliche Vollkommenheit', description: 'Perfektion der Götter', icon: '👑', cost: 100000, duration: 120, bonusType: 'abilityChance', bonusValue: 50, prerequisites: ['transcendence'], position: { x: 400, y: 350 }, tier: 7 },

    { id: 'reality_bending', name: 'Realitätsverzerrung', description: 'Verzerrung der Realität selbst', icon: '🌀', cost: 150000, duration: 150, bonusType: 'attack', bonusValue: 250, prerequisites: ['cosmic_awakening', 'eternal_wisdom'], position: { x: 150, y: 300 }, tier: 8 },
    { id: 'time_mastery', name: 'Zeit-Meisterschaft', description: 'Kontrolle über die Zeit', icon: '⏰', cost: 180000, duration: 180, bonusType: 'hp', bonusValue: 500, prerequisites: ['eternal_wisdom', 'divine_perfection'], position: { x: 650, y: 300 }, tier: 8 },
    { id: 'space_manipulation', name: 'Raum-Manipulation', description: 'Kontrolle über den Raum', icon: '🌍', cost: 200000, duration: 200, bonusType: 'defense', bonusValue: 300, prerequisites: ['cosmic_awakening', 'divine_perfection'], position: { x: 400, y: 250 }, tier: 8 },

    { id: 'creation_magic', name: 'Schöpfungsmagie', description: 'Magie der Schöpfung', icon: '✨', cost: 300000, duration: 250, bonusType: 'attack', bonusValue: 500, prerequisites: ['reality_bending', 'time_mastery'], position: { x: 100, y: 200 }, tier: 9 },
    { id: 'destruction_magic', name: 'Zerstörungsmagie', description: 'Magie der Zerstörung', icon: '💥', cost: 350000, duration: 300, bonusType: 'defense', bonusValue: 600, prerequisites: ['time_mastery', 'space_manipulation'], position: { x: 700, y: 200 }, tier: 9 },
    { id: 'life_magic', name: 'Lebensmagie', description: 'Magie des Lebens', icon: '🌱', cost: 400000, duration: 350, bonusType: 'hp', bonusValue: 1000, prerequisites: ['reality_bending', 'space_manipulation'], position: { x: 400, y: 150 }, tier: 9 },

    { id: 'omnipotence', name: 'Allmacht', description: 'Absolute Macht über alles', icon: '⚡', cost: 500000, duration: 400, bonusType: 'attack', bonusValue: 1000, prerequisites: ['creation_magic', 'destruction_magic'], position: { x: 200, y: 100 }, tier: 10 },
    { id: 'omniscience', name: 'Allwissenheit', description: 'Wissen über alles', icon: '🧠', cost: 600000, duration: 450, bonusType: 'experience', bonusValue: 500, prerequisites: ['destruction_magic', 'life_magic'], position: { x: 600, y: 100 }, tier: 10 },
    { id: 'omnipresence', name: 'Allgegenwart', description: 'Gegenwart überall', icon: '👁️', cost: 700000, duration: 500, bonusType: 'abilityChance', bonusValue: 100, prerequisites: ['creation_magic', 'life_magic'], position: { x: 400, y: 50 }, tier: 10 },
  ];

  // IDs verfügbarer Projekte im Store
  const availableProjectIds = new Set(researchProjects.map(p => p.id));
  // Sichtbare Nodes: nur jene, die tatsächlich im Store existieren
  const visibleSkillTree = skillTree.filter(n => availableProjectIds.has(n.id));

  // Berechne Preis-Multiplikator für Anzeige
  const completedProjects = researchProjects.filter(p => p.completed).length;
  const priceMultiplier = 1 + (completedProjects * 0.5);

  const getTimeRemaining = (project: any) => {
    if (!project.isActive || !project.startTime) return null;

    const startTime = new Date(project.startTime);
    const endTime = new Date(startTime.getTime() + project.duration * 60 * 1000);
    const now = new Date();
    const remainingMs = endTime.getTime() - now.getTime();

    if (remainingMs > 0) {
      const minutes = Math.floor(remainingMs / (1000 * 60));
      const seconds = Math.floor((remainingMs % (1000 * 60)) / 1000);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    return null;
  };

  const getProgress = (project: any) => {
    if (!project.isActive || !project.startTime) return 0;

    const startTime = new Date(project.startTime);
    const endTime = new Date(startTime.getTime() + project.duration * 60 * 1000);
    const now = new Date();
    const totalMs = project.duration * 60 * 1000;
    const elapsedMs = now.getTime() - startTime.getTime();

    return Math.min(100, Math.max(0, (elapsedMs / totalMs) * 100));
  };

  const getBonusIcon = (bonusType: string) => {
    switch (bonusType) {
      case 'attack': return '⚔️';
      case 'defense': return '🛡️';
      case 'hp': return '❤️';
      case 'experience': return '📚';
      case 'abilityChance': return '✨';
      default: return '🔧';
    }
  };

  const getBonusColor = (bonusType: string) => {
    switch (bonusType) {
      case 'attack': return 'from-red-500 to-orange-500';
      case 'defense': return 'from-blue-500 to-cyan-500';
      case 'hp': return 'from-green-500 to-emerald-500';
      case 'experience': return 'from-purple-500 to-pink-500';
      case 'abilityChance': return 'from-yellow-500 to-orange-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  // Hilfsfunktionen für den Skill-Tree
  const getNodeStatus = (nodeId: string) => {
    const project = researchProjects.find(p => p.id === nodeId);
    if (!project) return 'locked';
    if (project.completed) return 'completed';
    if (project.isActive) return 'active';

    const node = skillTree.find(n => n.id === nodeId);
    if (!node) return 'locked';

    if (node.tier === 1) return 'available';

    const prerequisitesMet = node.prerequisites.every(prereqId =>
      researchProjects.find(p => p.id === prereqId)?.completed
    );

    return prerequisitesMet ? 'available' : 'locked';
  };

  const isNodeUnlocked = (nodeId: string) => {
    const status = getNodeStatus(nodeId);
    return status === 'available' || status === 'active' || status === 'completed';
  };

  const renderConnection = (fromNode: SkillNode, toNode: SkillNode) => {
    const fromStatus = getNodeStatus(fromNode.id);
    const toStatus = getNodeStatus(toNode.id);
    const isActive = fromStatus === 'completed' && (toStatus === 'available' || toStatus === 'active' || toStatus === 'completed');

    return (
      <line
        key={`${fromNode.id}-${toNode.id}`}
        x1={fromNode.position.x + 40}
        y1={fromNode.position.y + 40}
        x2={toNode.position.x + 40}
        y2={toNode.position.y + 40}
        stroke={isActive ? '#10b981' : '#374151'}
        strokeWidth="3"
        strokeDasharray={isActive ? '0' : '5,5'}
        className="transition-all duration-500"
      />
    );
  };

  // Zoom-Funktionen
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.2, 1.5));
  };

  const handleZoomOut = () => {
    // Verhindere zu kleines Zoom (besonders auf Mobile)
    setZoomLevel(prev => Math.max(prev - 0.2, 1.0));
  };

  const handleResetView = () => {
    setZoomLevel(defaultZoom);
    setPanOffset({ x: 0, y: 0 });
  };

  // Berechnet die absolute Bildschirmposition einer Node (für das Popup)
  const getDetailPanelStyle = (node: SkillNode) => {
    const svgW = 800;
    const svgH = 1200;
    const vX = node.position.x + 40;
    const vY = node.position.y + 40;

    const transX = (panOffset.x - 20) + (vX - svgW / 2) * zoomLevel;
    const transY = (panOffset.y) + (vY - svgH / 2) * zoomLevel;

    const left = `calc(50% + ${transX}px)`;
    const top = `calc(50% + ${transY}px)`;

    return {
      left,
      top,
      transform: 'translate(-50%, -120%)',
      position: 'absolute' as const,
      zIndex: 40,
      width: '18rem'
    };
  };

  // Zentriert die Ansicht auf eine Node-Position
  const centerOnNode = (position: { x: number; y: number }) => {
    const centerX = position.x + 40;
    const centerY = position.y + 40;

    const targetPanX = 400 - centerX;
    const targetPanY = 600 - centerY;

    setPanOffset({ x: targetPanX, y: targetPanY });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setLastMousePos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      const deltaX = e.clientX - lastMousePos.x;
      const deltaY = e.clientY - lastMousePos.y;
      setPanOffset(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      setLastMousePos({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const zoomDelta = e.deltaY > 0 ? -0.08 : 0.08;
    setZoomLevel(prev => Math.max(1.0, Math.min(3.0, prev + zoomDelta)));
  };

  // Touch-Gesten für Mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    // Verhindere, dass das parent Modal scrollt
    e.stopPropagation();
    if (e.touches.length === 1) {
      setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    } else if (e.touches.length === 2) {
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      setTouchDistance(distance);
      setTouchInitialZoom(zoomLevel);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    // Verhindere, dass das parent Modal scrollt
    e.stopPropagation();
    // Nur dann preventDefault, wenn wir aktiv panning/pinchen (sonst Page-Scroll erlauben)
    const shouldBlock = e.touches.length > 1 || touchStart !== null;
    if (shouldBlock) e.preventDefault();

    if (e.touches.length === 1 && touchStart) {
      const deltaX = e.touches[0].clientX - touchStart.x;
      const deltaY = e.touches[0].clientY - touchStart.y;
      setPanOffset(prev => ({
        x: prev.x + deltaX / Math.max(0.5, zoomLevel),
        y: prev.y + deltaY / Math.max(0.5, zoomLevel)
      }));
      setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    } else if (e.touches.length === 2 && touchDistance && touchInitialZoom !== null) {
      const newDistance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const scale = newDistance / touchDistance;
      const newZoom = Math.max(1.0, Math.min(3.0, touchInitialZoom * scale));
      setZoomLevel(newZoom);
    }
  };

  const handleTouchEnd = (e?: React.TouchEvent) => {
    if (e) e.stopPropagation();
    // Double-tap detection
    const now = Date.now();
    if (lastTapTime && now - lastTapTime < 300) {
      // Double tap detected -> toggle zoom
      setZoomLevel(prev => prev > defaultZoom ? defaultZoom : Math.min(1.1, 1.1));
      setLastTapTime(null);
    } else {
      setLastTapTime(now);
    }

    // Reset pinch/touch state
    setTouchStart(null);
    setTouchDistance(null);
    setTouchInitialZoom(null);
  };

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative bg-gradient-to-br from-gray-900 via-slate-800 to-gray-900 rounded-2xl p-4 sm:p-8 border border-gray-600 min-h-[400px] sm:min-h-[600px] overflow-hidden"
      >
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#374151" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div
          ref={containerRef}
          className="relative w-full h-[80vh] sm:h-[90vh] overflow-hidden bg-gray-800 rounded-lg border border-gray-600 touch-manipulation"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClick={() => { setSelectedNode(null); handleResetView(); }}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <svg
            width="100%"
            height="100%"
            preserveAspectRatio="xMidYMid meet"
            className="absolute top-1/2 left-1/2 touch-pan-x touch-pan-y transition-transform duration-200"
            style={{
              transform: `translate(calc(-50% + ${panOffset.x - 20}px), calc(-50% + ${panOffset.y}px)) scale(${zoomLevel})`,
              transformOrigin: 'center center'
            }}
            viewBox="0 0 800 1200"
          >
            {/* Verbindungslinien: nur zwischen sichtbaren Nodes */}
            {visibleSkillTree.map(node => 
              node.prerequisites.map(prereqId => {
                const prereqNode = visibleSkillTree.find(n => n.id === prereqId);
                return prereqNode ? renderConnection(prereqNode, node) : null;
              })
            ).flat()}
            
            {/* Skill-Nodes (nur sichtbare) */}
            {visibleSkillTree.map(node => {
              const status = getNodeStatus(node.id);
              const project = researchProjects.find(p => p.id === node.id);
              const isSelected = selectedNode === node.id;
              const isHovered = hoveredNode === node.id;

              return (
                <g key={node.id}>
                  <motion.circle
                    cx={node.position.x + 40}
                    cy={node.position.y + 40}
                    r={isSelected ? nodeRadius + 12 : (isHovered ? nodeRadius + 6 : nodeRadius)}
                    fill={
                      status === 'completed' ? '#10b981' :
                      status === 'active' ? '#3b82f6' :
                      status === 'available' ? '#8b5cf6' : '#374151'
                    }
                    stroke={isSelected ? '#fbbf24' : '#6b7280'}
                    strokeWidth={isSelected ? 5 : 3}
                    className="cursor-pointer transition-all duration-300"
                    onClick={(e: React.MouseEvent<SVGCircleElement, MouseEvent>) => {
                      e.stopPropagation();
                      if (isMobileView) {
                        setSelectedNode(prev => prev === node.id ? null : node.id);
                        return;
                      }

                      if (selectedNode === node.id) {
                        setSelectedNode(null);
                        handleResetView();
                      } else {
                        setSelectedNode(node.id);
                        setZoomLevel(1.1);
                        centerOnNode(node.position);
                      }
                    }}
                    onTouchStart={() => setHoveredNode(node.id)}
                    onTouchEnd={() => setHoveredNode(null)}
                    onMouseEnter={() => setHoveredNode(node.id)}
                    onMouseLeave={() => setHoveredNode(null)}
                    style={{
                      filter: status === 'locked' ? 'grayscale(100%)' : 'none',
                      opacity: status === 'locked' ? 0.5 : 1
                    }}
                  />

                  <text
                    x={node.position.x + 40}
                    y={node.position.y + 52}
                    textAnchor="middle"
                    fontSize="28"
                    className="pointer-events-none select-none"
                  >
                    {node.icon}
                  </text>

                  <text
                    x={node.position.x + 40}
                    y={node.position.y + 92}
                    textAnchor="middle"
                    fontSize="10"
                    className="pointer-events-none"
                    fill={coins >= node.cost ? '#34D399' : '#F87171'}
                  >
                    {formatNumber(node.cost)}
                  </text>

                  <text
                    x={node.position.x + 40}
                    y={node.position.y + 104}
                    textAnchor="middle"
                    fontSize="9"
                    className="pointer-events-none text-gray-300"
                    fill="#9CA3AF"
                  >
                    {node.duration}m
                  </text>

                  {status === 'completed' && (
                    <text
                      x={node.position.x + 65}
                      y={node.position.y + 25}
                      fontSize="16"
                      className="pointer-events-none select-none"
                    >
                      ✅
                    </text>
                  )}

                  {status === 'active' && (
                    <text
                      x={node.position.x + 65}
                      y={node.position.y + 25}
                      fontSize="16"
                      className="pointer-events-none select-none animate-pulse"
                    >
                      🔬
                    </text>
                  )}
                </g>
              );
            })}
          </svg>

        </div>

        {selectedNode && (() => {
          // Selected node must be visible; fallback to null if it's not available in store
          const node = skillTree.find(n => n.id === selectedNode);
          if (!node) return null;
          const project = researchProjects.find(p => p.id === selectedNode);
          const status = getNodeStatus(selectedNode);

          if (isMobileView) {
            return (
              <motion.div
                initial={{ y: 300, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 300, opacity: 0 }}
                className="fixed left-0 right-0 bottom-0 z-50 bg-gradient-to-br from-slate-800 to-slate-900 rounded-t-xl p-4 border-t border-slate-600 text-white shadow-2xl max-h-[70vh] overflow-auto touch-manipulation"
                onTouchStart={(e) => {
                  bottomSheetTouchStartY.current = e.touches?.[0]?.clientY ?? null;
                }}
                onTouchMove={(e) => {
                  if (!bottomSheetTouchStartY.current) return;
                  const currentY = e.touches?.[0]?.clientY ?? 0;
                  const delta = currentY - (bottomSheetTouchStartY.current ?? 0);
                  if (delta > 80) {
                    // Swipe-Down detected -> schließe Bottom-Sheet
                    setSelectedNode(null);
                    bottomSheetTouchStartY.current = null;
                  }
                }}
                onTouchEnd={() => bottomSheetTouchStartY.current = null}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">{node.icon}</div>
                    <div>
                      <h4 className="font-bold text-base text-white">{node.name}</h4>
                      <p className="text-xs text-gray-400">Tier {node.tier}</p>
                    </div>
                  </div>
                  <button onClick={() => { setSelectedNode(null); handleResetView(); }} className="text-white text-xl p-1">✕</button>
                </div>

                <p className="text-sm text-gray-300 mb-3">{node.description}</p>

                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="bg-slate-700 rounded-lg p-2 text-center">
                    <div className="text-xs text-gray-400">Kosten</div>
                    <div className="font-bold text-sm">{formatNumber(node.cost)}</div>
                  </div>
                  <div className="bg-slate-700 rounded-lg p-2 text-center">
                    <div className="text-xs text-gray-400">Dauer</div>
                    <div className="font-bold text-sm">{node.duration} Min</div>
                  </div>
                </div>

                <div className="bg-purple-600/20 rounded-lg p-2 text-center mb-3">
                  <div className="text-sm text-purple-300">Bonus</div>
                  <div className="font-bold text-sm text-purple-100">+{node.bonusValue} {getBonusIcon(node.bonusType)}</div>
                </div>

                {status === 'available' && (
                  <button
                    onClick={() => {
                      if (coins < node.cost) return;
                      startResearch(node.id);
                      setSelectedNode(null);
                      handleResetView();
                    }}
                    className={`w-full py-3 rounded-lg text-sm ${coins >= node.cost ? 'bg-purple-600 text-white' : 'bg-gray-600 text-gray-400'}`}
                  >
                    {coins >= node.cost ? '🔬 Erforschen' : '💰 Zu wenig Münzen'}
                  </button>
                )}

              </motion.div>
            );
          }

          const panelStyle = getDetailPanelStyle(node);
          return (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              style={panelStyle}
              className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-3 sm:p-4 border border-slate-600 text-white shadow-2xl"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg sm:text-xl">{node.icon}</span>
                  <div>
                    <h4 className="font-bold text-sm sm:text-base text-white">{node.name}</h4>
                    <p className="text-xs text-gray-400">Tier {node.tier}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedNode(null)}
                  className="sm:hidden text-gray-400 hover:text-white text-base p-1"
                >
                  ✕
                </button>
              </div>

              <p className="text-xs text-gray-300 mb-2">{node.description}</p>

              {status === 'active' && project && (
                <div className="mb-2">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-blue-200">Fortschritt</span>
                    <span className="text-blue-200 font-bold">{getTimeRemaining(project)}</span>
                  </div>
                  <div className="w-full bg-gray-600 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${getProgress(project)}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 mb-2">
                <div className="bg-slate-700 rounded-lg p-1.5 text-center">
                  <div className="text-xs text-gray-400">Kosten</div>
                  <div className="font-bold text-xs">{formatNumber(node.cost)}</div>
                </div>
                <div className="bg-slate-700 rounded-lg p-1.5 text-center">
                  <div className="text-xs text-gray-400">Dauer</div>
                  <div className="font-bold text-xs">{node.duration} Min</div>
                </div>
              </div>

              <div className="bg-purple-600/20 rounded-lg p-1.5 text-center mb-2">
                <div className="text-xs text-purple-300">Bonus</div>
                <div className="font-bold text-xs text-purple-100">+{node.bonusValue} {getBonusIcon(node.bonusType)}</div>
              </div>

              {status === 'available' && (
                <div className="space-y-2">
                  <button
                    onClick={() => {
                      if (coins < node.cost) {
                        alert('Du hast nicht genug Münzen.');
                        return;
                      }
                      startResearch(node.id);
                    }}
                    className={`w-full py-1.5 px-2 rounded-lg font-medium transition-all text-xs ${coins >= node.cost ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'bg-gray-600 text-gray-400 cursor-not-allowed'}`}>
                    {coins >= node.cost ? '🔬 Erforschen' : '💰 Zu wenig Münzen'}
                  </button>
                </div>
              )}

            </motion.div>
          );
        })()}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden bg-gradient-to-br from-purple-600/20 via-blue-600/20 to-purple-600/20 rounded-xl p-3 text-white border border-purple-400/30 backdrop-blur-sm"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 via-transparent to-blue-500/10 animate-pulse"></div>
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-400 via-blue-400 to-purple-400"></div>

        <div className="relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-3">
            <div className="flex items-center gap-2">
              <div className="relative">
                <div className="text-2xl sm:text-xl animate-bounce">🌟</div>
                <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-yellow-400 rounded-full animate-ping"></div>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
                  Forschungsbaum der Begleiter
                </h3>
                <p className="text-purple-200 text-xs sm:text-sm opacity-80">
                  Entwickle mächtige Fähigkeiten durch strategische Forschung
                </p>
              </div>
            </div>

            <div className="flex sm:flex-row flex-col sm:items-center sm:gap-2 gap-1">
              {completedProjects > 0 && (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-lg px-3 py-1 border border-orange-400/30 text-center"
                >
                  <div className="text-xs sm:text-sm text-orange-200 font-medium">Abgeschlossen</div>
                  <div className="text-sm sm:text-base font-bold text-orange-100">{completedProjects}</div>
                </motion.div>
              )}

              {skillTreeResetInfo.resetCount > 0 && (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-lg px-3 py-1 border border-purple-400/30 text-center"
                >
                  <div className="text-xs sm:text-sm text-purple-200 font-medium">Zurücksetzungen</div>
                  <div className="text-sm sm:text-base font-bold text-purple-100">{skillTreeResetInfo.resetCount}</div>
                </motion.div>
              )}
            </div>
          </div>

          {/* Mobile: kompakter Grid (3 Spalten) damit alle Karten sichtbar sind, Desktop: 5 Spalten */}
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-1 py-1">
            {Object.entries(globalBonuses).map(([bonusType, value], index) => (
              <motion.div
                key={bonusType}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.06 }}
                className="relative group bg-gradient-to-br from-white/6 to-white/4 rounded-lg p-1 text-center border border-white/10 min-w-[64px] sm:p-2 sm:rounded-lg sm:min-w-0 text-xs"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/12 to-blue-500/12 rounded-lg opacity-0 group-hover:opacity-80 transition-opacity duration-300"></div>
                <div className="relative z-10 flex flex-col items-center justify-center">
                  <div className="text-xl sm:text-lg mb-1 animate-pulse">{getBonusIcon(bonusType)}</div>
                  <div className="text-[11px] sm:text-xs font-medium capitalize text-purple-200">
                    {bonusType === 'hp' ? 'HP' : 
                     bonusType === 'abilityChance' ? 'Fähigkeiten' : 
                     bonusType === 'attack' ? 'Angriff' :
                     bonusType === 'defense' ? 'Verteidigung' :
                     bonusType === 'experience' ? 'Erfahrung' : bonusType}
                  </div>
                  <div className="text-xs sm:text-sm font-bold text-white">+{value}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {skillTreeResetInfo.canReset && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden bg-gradient-to-r from-yellow-500/20 via-orange-500/20 to-red-500/20 rounded-xl p-4 border border-yellow-400/30 backdrop-blur-sm"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/10 via-transparent to-red-500/10 animate-pulse"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400"></div>

          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="text-2xl animate-bounce">🌟</div>
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-ping"></div>
                </div>
                <div>
                  <h3 className="text-lg font-bold bg-gradient-to-r from-yellow-200 to-orange-200 bg-clip-text text-transparent">
                    Forschungsbaum vollständig abgeschlossen!
                  </h3>
                  <p className="text-yellow-200 text-xs opacity-80">
                    Starte den Forschungsbaum neu und erhalte 100% bessere Belohnungen
                  </p>
                </div>
              </div>

              <button
                onClick={() => {
                  if (window.confirm('Möchtest du den Forschungsbaum wirklich zurücksetzen? Alle aktuellen Boni werden permanent gespeichert und um 100% verbessert.')) {
                    resetSkillTree();
                  }
                }}
                className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-4 py-2 rounded-lg font-bold transition-all duration-300 hover:scale-105 shadow-lg"
              >
                🔄 Forschungsbaum zurücksetzen
              </button>
            </div>
          </div>
        </motion.div>
      )}

    </div>
  );
};

export default CompanionResearch;
 